import { store } from '@/store/index';
import { ref, Ref } from 'vue';

import { Sds, SdsEditHistoryResponse } from '@/interfaces/model/general/sds';
import { User } from '@/interfaces/vuex/user';
import { NewSds } from '@/lib/constructor/model/general/sds';
import {
  convertApiResponseToFrontend,
  convertFrontendToApiRequest,
} from '@/utils/sdsDataConverter';
import {
  readAssertedCompany,
  readAssertedCompanyId,
} from '@/store/main/getters';
import { commitSetCompany } from '@/store/main/mutations';

import {
  DeleteCompanySdsesRequestBody,
  GetCompanySdsesCountRequestQuery,
  GetCompanySdsesRequestQuery,
  UpdateCompanySdsesRequestBody,
  UpdateCompanySdsRequestBody,
  DownloadCompanySdsesCSVRequestQuery,
  DownloadRiskAssessmentRecomputeCSVRequestQuery,
  UploadCompanySdsesCSVRequestBody,
  DownloadCompanySdsesPDFRequestQuery,
  UploadCompanySdsesCSVResponse,
  UploadRiskAssessmentRecomputeCSVResponse,
} from '@/interfaces/api/general/company';
import {
  dispatchDeleteCompanySdses,
  dispatchGetCompanySds,
  dispatchGetCompanySdsPdf,
  dispatchGetCompanySdses,
  dispatchGetCompanySdsesCount,
  dispatchUpdateCompanySds,
  dispatchUpdateCompanySdses,
  dispatchDownloadCompanySdsesCSV,
  dispatchDownloadRiskAssessmentRecomputeCSV,
  dispatchUploadRiskAssessmentRecomputeCSV,
  dispatchUploadCompanySdsesCSV,
  dispatchDownloadCompanySdsesPDFBulk,
  dispatchGetPdfDownloadCount,
  dispatchGetEditHistories,
} from '@/store/main/actionsMain';
import { DepartmentsState } from '@/interfaces/vuex/department';
import { TagResponse } from '@/interfaces/model/general/tag';

export interface SortState {
  [key: string]: 'asc' | 'desc' | 'none';
}

type UseSdsComposable = {
  sds: Ref<Sds>;
  sdses: Ref<Sds[]>;
  total: Ref<number>;
  pdfBlob: Ref<string>;
  updateCompanySds: (
    companyId: string,
    fileId: string,
    sds: Sds,
    department: DepartmentsState,
    tags: TagResponse[],
    userProfile: User
  ) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

//TODO: ↑のtypeとマージするか型推論ベースにする
// anyを回避するためのtype、useSdsが複数箇所で利用されているためそれぞれで型をつけられるようにしている
export type ActualUseSdsComposable = UseSdsComposable & {
  fetchCompanySdsesCount: (data: {
    companyId: string;
    data: GetCompanySdsesCountRequestQuery;
  }) => Promise<void>;
  fetchCompanySdsesCountWithCondition: (data: {
    companyId: string;
    data: GetCompanySdsesCountRequestQuery;
  }) => Promise<void>;
  fetchCompanySdses: (data: {
    companyId: string;
    data: GetCompanySdsesRequestQuery;
  }) => Promise<void>;
};

export const useSdsComposable = (): UseSdsComposable => {
  const sds = ref<Sds>({ ...NewSds });
  const sdses = ref<Array<Sds>>([]);
  const wholeSds = ref<Array<Sds>>([]);
  const total = ref<number>(0);
  const pdfBlob = ref<string>('');
  const pdfDownloadCount = ref<number>(0);
  const pdfMonthlyLimit = ref<number>(0);
  const editHistories = ref<Array<SdsEditHistoryResponse>>([]);

  const fetchDownloadPdfCount = async (data: {
    companyId: string;
    signal?: AbortSignal;
  }): Promise<void> => {
    pdfDownloadCount.value = 0;
    pdfMonthlyLimit.value = 0;
    const res = await dispatchGetPdfDownloadCount(store, data);
    pdfDownloadCount.value = res.data.download_count;
    pdfMonthlyLimit.value = res.data.monthly_limit;
  };

  const fetchCompanySdsesCount = async (data: {
    companyId: string;
    data: GetCompanySdsesCountRequestQuery;
    signal?: AbortSignal;
  }): Promise<void> => {
    const res = await dispatchGetCompanySdsesCount(store, data);
    if (res) {
      commitSetCompany(store, {
        ...readAssertedCompany(store),
        uploaded_file_number: res.data.total,
      });
    }
  };

  const fetchCompanySdsesCountWithCondition = async (data: {
    companyId: string;
    data: GetCompanySdsesCountRequestQuery;
    signal?: AbortSignal;
  }): Promise<void> => {
    const res = await dispatchGetCompanySdsesCount(store, data);
    if (res) {
      total.value = res.data.total;
    }
  };

  const fetchSdsPdf = async (data: {
    companyId: string;
    sdsId: string;
  }): Promise<void> => {
    const res = await dispatchGetCompanySdsPdf(store, {
      companyId: data.companyId,
      fileUuid: data.sdsId,
    });
    if (res) {
      pdfBlob.value = `data:application/pdf;base64,${res.data.data}`;
    }
  };

  const fetchSdsEditHistories = async (data: {
    companyId: string;
    sdsId: string;
  }): Promise<void> => {
    const res = await dispatchGetEditHistories(store, data);
    if (res) {
      editHistories.value = res.data;
    }
  };

  const fetchCompanySdses = async (data: {
    companyId: string;
    data: GetCompanySdsesRequestQuery;
    signal?: AbortSignal;
  }): Promise<void> => {
    if (data.data.limit > 500) {
      const limit = 500;
      let offset = 0;
      let hasMoreData = true;

      wholeSds.value.splice(0);

      while (hasMoreData) {
        data.data.limit = limit;
        data.data.page = offset;

        const res = await dispatchGetCompanySdses(store, data);

        if (res && res.data.sdses && res.data.sdses.length > 0) {
          wholeSds.value.push(
            ...res.data.sdses.map((x) => {
              return { ...x };
            })
          );

          offset += 1;
        } else {
          hasMoreData = false;
        }

        if (limit * offset > 9999) {
          hasMoreData = false;
        }
      }
    } else {
      sdses.value.splice(0);
      const res = await dispatchGetCompanySdses(store, data);
      sdses.value.push(
        ...res.data.sdses.map((x) => {
          return { ...x };
        })
      );
    }
  };

  const fetchCompanySds = async (data: { sdsId: string }): Promise<void> => {
    const res = await dispatchGetCompanySds(store, {
      sdsId: data.sdsId,
      companyId: readAssertedCompanyId(store),
    });
    if (res) {
      sds.value.tags = res.data.sds.tags;
      sds.value.product_code_list = res.data.sds.product_code_list;
      sds.value.id = res.data.sds.id;
      sds.value.uuid = res.data.sds.uuid;
      sds.value.name = res.data.sds.name;
      sds.value.replaced_name = res.data.sds.replaced_name;
      sds.value.note = res.data.sds.note;
      sds.value.admin_note = res.data.sds.admin_note;
      sds.value.company_id = res.data.sds.company_id;
      sds.value.departments = res.data.sds.departments;
      sds.value.attachments = res.data.sds.attachments.map((att) => ({
        ...att,
        gcs_signed_url: att.gcs_signed_url || '',
      }));
      sds.value.status_id = res.data.sds.status_id;
      sds.value.assignee_users = res.data.sds.assignee_users;
      sds.value.updated_by = res.data.sds.updated_by;
      sds.value.timestamp = res.data.sds.timestamp;
      sds.value.published_at = res.data.sds.published_at;
      sds.value.revised_at = res.data.sds.revised_at;
      sds.value.revision_status = res.data.sds.revision_status;
      sds.value.created_at = res.data.sds.created_at;
      sds.value.created_user = res.data.sds.created_user;
      sds.value.updated_at = res.data.sds.updated_at;
      sds.value.supplier_confirmed_at = res.data.sds.supplier_confirmed_at;
      sds.value.deleted_at = res.data.sds.deleted_at;
      sds.value.assessed_at = res.data.sds.assessed_at;
      sds.value.sds_section14 = res.data.sds.sds_section14;
      sds.value.sds_section8 = res.data.sds.sds_section8;
      sds.value.sds_type = res.data.sds.sds_type;
      sds.value.dynamic_list_result = res.data.sds.dynamic_list_result;
      sds.value.same_name_sds_info = res.data.sds.same_name_sds_info;
      sds.value.assistant_log_cross_checks =
        res.data.sds.assistant_log_cross_checks.map((x) => {
          const { id, sds_id, started_at, ended_at, deleted_at } = x;
          return {
            id,
            sds_id,
            started_at,
            ended_at,
            deleted_at,
          };
        });
      sds.value.user_reanalysis_comment =
        res.data.sds.user_reanalysis_comment || [];
      sds.value.sds_products = res.data.sds.sds_products.map((x) => {
        return {
          id: x.id,
          sds_id: x.sds_id,
          name: x.name,
          recommended_use: x.recommended_use,
          restriction: x.restriction,
          recommended_use_restriction: x.recommended_use_restriction,
          created_at: x.created_at,
          updated_at: x.updated_at,
          deleted_at: x.deleted_at,
          sds_section1_importers: x.sds_section1_importers.map((im) => {
            return {
              id: im.id,
              sds_id: im.sds_id,
              sds_product_id: im.sds_product_id,
              name: im.name,
              phone: im.phone,
              address: im.address,
              department: im.department,
              email: im.email,
              created_at: im.created_at,
              updated_at: im.updated_at,
              deleted_at: im.deleted_at,
            };
          }),
          sds_section1_manufacturers: x.sds_section1_manufacturers.map((im) => {
            return {
              id: im.id,
              sds_id: im.sds_id,
              sds_product_id: im.sds_product_id,
              name: im.name,
              phone: im.phone,
              address: im.address,
              department: im.department,
              email: im.email,
              created_at: im.created_at,
              updated_at: im.updated_at,
              deleted_at: im.deleted_at,
            };
          }),
          sds_section2_list: x.sds_section2_list.sort((a, b) =>
            a.item_id > b.item_id ? 1 : -1
          ),
          sds_section2_label: {
            id: x.sds_section2_label.id,
            sds_id: x.sds_section2_label.sds_id,
            sds_product_id: x.sds_section2_label.sds_product_id,
            bomb: x.sds_section2_label.bomb,
            fire: x.sds_section2_label.fire,
            fire_circle: x.sds_section2_label.fire_circle,
            gas: x.sds_section2_label.gas,
            corrosion: x.sds_section2_label.corrosion,
            toxic: x.sds_section2_label.toxic,
            exclamation: x.sds_section2_label.exclamation,
            health: x.sds_section2_label.health,
            environment: x.sds_section2_label.environment,
            signal_words: x.sds_section2_label.signal_words,
            signal_word_id: x.sds_section2_label.signal_word_id,
            created_at: x.sds_section2_label.created_at,
            updated_at: x.sds_section2_label.updated_at,
            deleted_at: x.sds_section2_label.deleted_at,
          },
          sds_section3: convertApiResponseToFrontend(x.sds_section3),
          sds_section8_acgih_list: x.sds_section8_acgih_list,
          sds_section8_jsoh_list: x.sds_section8_jsoh_list,
          sds_section8_dnel_dmel_list: x.sds_section8_dnel_dmel_list,
          sds_section8_exposure_control_list:
            x.sds_section8_exposure_control_list,
          sds_section9_list: x.sds_section9_list.map((s9) => {
            return {
              id: s9.id,
              sds_id: s9.sds_id,
              sds_product_id: s9.sds_product_id,
              condition: s9.condition,
              physical_condition: s9.physical_condition,
              form: s9.form,
              created_at: s9.created_at,
              updated_at: s9.updated_at,
              deleted_at: s9.deleted_at,
            };
          }),
          sds_section9_flash_point_list: x.sds_section9_flash_point_list.map(
            (f) => {
              return {
                id: f.id,
                sds_id: f.sds_id,
                sds_product_id: f.sds_product_id,
                flash_point_min: f.flash_point_min,
                flash_point_max: f.flash_point_max,
                flash_point_inequality_sign: f.flash_point_inequality_sign,
                unit_id: f.unit_id,
                measurement_method: f.measurement_method,
                created_at: f.created_at,
                updated_at: f.updated_at,
                deleted_at: f.deleted_at,
              };
            }
          ),
          sds_section9_boiling_point_list:
            x.sds_section9_boiling_point_list.map((f) => {
              return {
                id: f.id,
                sds_id: f.sds_id,
                sds_product_id: f.sds_product_id,
                boiling_point_min: f.boiling_point_min,
                boiling_point_max: f.boiling_point_max,
                boiling_point_inequality_sign: f.boiling_point_inequality_sign,
                unit_id: f.unit_id,
                measurement_method: f.measurement_method,
                created_at: f.created_at,
                updated_at: f.updated_at,
                deleted_at: f.deleted_at,
              };
            }),
          sds_section15_list: x.sds_section15_list.map((section15) => {
            return {
              id: section15.id,
              sds_id: section15.sds_id,
              sds_product_id: section15.sds_product_id,
              law_id: section15.law_id,
              regulation1_id: section15.regulation1_id,
              regulation2_id: section15.regulation2_id,
              regulation3_id: section15.regulation3_id,
              regulation4_id: section15.regulation4_id,
              regulation5_id: section15.regulation5_id,
              applicable_id: section15.applicable_id,
              text: section15.text,
              created_at: section15.created_at,
              updated_at: section15.updated_at,
              deleted_at: section15.deleted_at,
              material_list: section15.material_list.map((material) => {
                return {
                  id: material.id,
                  sds_id: material.sds_id,
                  sds_section15_id: material.sds_section15_id,
                  material_name: material.material_name,
                  created_at: material.created_at,
                  updated_at: material.updated_at,
                  deleted_at: material.deleted_at,
                  cas_list: material.cas_list.map((cas) => {
                    return {
                      id: cas.id,
                      sds_id: cas.sds_id,
                      sds_section15_material_id: cas.sds_section15_material_id,
                      cas_number: cas.cas_number,
                      created_at: cas.created_at,
                      updated_at: cas.updated_at,
                      deleted_at: cas.deleted_at,
                    };
                  }),
                };
              }),
            };
          }),
          sds_risk_assessment_results: x.sds_risk_assessment_results,
        };
      });
    }
  };

  const updateCompanySds = async (
    companyId: string,
    fileId: string,
    sds: Sds,
    department: DepartmentsState,
    tags: TagResponse[],
    userProfile: User
  ): Promise<void> => {
    await dispatchUpdateCompanySds(store, {
      companyId: companyId,
      fileId: fileId,
      form: convertSdsToSdsRequestBody(sds, department, tags, userProfile),
    });
  };

  const updateCompanySdses = async (data: {
    companyId: string;
    data: UpdateCompanySdsesRequestBody;
  }): Promise<void> => {
    await dispatchUpdateCompanySdses(store, data);
  };

  const deleteCompanySdses = async (data: {
    companyId: string;
    data: DeleteCompanySdsesRequestBody;
  }): Promise<void> => {
    await dispatchDeleteCompanySdses(store, data);
  };
  const downloadSelectedSdsesCSV = async (data: {
    companyId: string;
    data: DownloadCompanySdsesCSVRequestQuery;
  }): Promise<Blob> => {
    const response = await dispatchDownloadCompanySdsesCSV(store, data);
    const responseData: Blob = response.data;
    return responseData;
  };

  const downloadSelectedRiskReassessmentCSV = async (data: {
    companyId: string;
    data: DownloadRiskAssessmentRecomputeCSVRequestQuery;
  }): Promise<string> => {
    const response = await dispatchDownloadRiskAssessmentRecomputeCSV(
      store,
      data
    );
    return response.data;
  };

  const uploadSelectedSdsesCSV = async (data: {
    companyId: string;
    data: UploadCompanySdsesCSVRequestBody;
  }): Promise<UploadCompanySdsesCSVResponse> => {
    const response = await dispatchUploadCompanySdsesCSV(store, {
      ...data,
    });
    const responseData: UploadCompanySdsesCSVResponse = response.data;
    return responseData;
  };

  const uploadSelectedRiskReassessmentCSV = async (data: {
    companyId: string;
    data: FormData;
  }): Promise<UploadRiskAssessmentRecomputeCSVResponse> => {
    const response = await dispatchUploadRiskAssessmentRecomputeCSV(
      store,
      data
    );
    return response.data;
  };

  const downloadSelectedSdsesPDFBulk = async (data: {
    companyId: string;
    data: DownloadCompanySdsesPDFRequestQuery;
  }): Promise<void> => {
    const res = await dispatchDownloadCompanySdsesPDFBulk(store, data);
    return res.data;
  };

  return {
    sds,
    sdses,
    total,
    pdfBlob,
    wholeSds,
    pdfDownloadCount,
    pdfMonthlyLimit,
    editHistories,
    fetchCompanySds,
    fetchCompanySdses,
    fetchSdsPdf,
    fetchCompanySdsesCount,
    fetchCompanySdsesCountWithCondition,
    updateCompanySds,
    deleteCompanySdses,
    updateCompanySdses,
    downloadSelectedSdsesCSV,
    downloadSelectedRiskReassessmentCSV,
    uploadSelectedSdsesCSV,
    uploadSelectedRiskReassessmentCSV,
    fetchDownloadPdfCount,
    downloadSelectedSdsesPDFBulk,
    fetchSdsEditHistories,
  };
};

const convertSdsToSdsRequestBody = (
  sds: Sds,
  departments: DepartmentsState,
  tags: TagResponse[],
  userProfile: User
): UpdateCompanySdsRequestBody => ({
  name: sds.name,
  department_id_list: departments.map((d) => d.uuid),
  tag_id_list: tags.map((t) => t.id.toString()),
  assignee_user_id_list: sds.assignee_users.map((u) => u.uuid),
  product_code_list: sds.product_code_list,
  note: sds.note,
  attachments: sds.attachments,
  status_id: sds.status_id,
  published_at: sds.published_at,
  revised_at: sds.revised_at,
  assessed_at: sds.assessed_at,
  updated_by: userProfile.uuid,
  supplier_confirmed_at: sds.supplier_confirmed_at,
  deleted_at: sds.deleted_at,
  sds_section14: {
    id: sds.sds_section14.id,
    sds_id: sds.sds_section14.sds_id,
    un_number_list: sds.sds_section14.un_number_list,
    deleted_at: sds.sds_section14.deleted_at,
  },
  sds_section8: {
    id: sds.sds_section8.id,
    sds_id: sds.sds_section8.sds_id,
    skin_protection: sds.sds_section8.skin_protection,
    protective_equipment_of_skin: sds.sds_section8.protective_equipment_of_skin,
    deleted_at: sds.sds_section8.deleted_at,
  },
  products: sds.sds_products.map((x) => ({
    id: x.id,
    sds_id: x.sds_id,
    name: x.name,
    recommended_use: x.recommended_use,
    restriction: x.restriction,
    recommended_use_restriction: x.recommended_use_restriction,
    deleted_at: x.deleted_at,
    sds_section1_importers: x.sds_section1_importers.map((im) => ({
      id: im.id,
      sds_id: im.sds_id,
      sds_product_id: im.sds_product_id,
      name: im.name,
      phone: im.phone,
      address: im.address,
      department: im.department,
      email: im.email,
      deleted_at: im.deleted_at,
    })),
    sds_section1_manufacturers: x.sds_section1_manufacturers.map((im) => ({
      id: im.id,
      sds_id: im.sds_id,
      sds_product_id: im.sds_product_id,
      name: im.name,
      phone: im.phone,
      address: im.address,
      department: im.department,
      email: im.email,
      deleted_at: im.deleted_at,
    })),
    sds_section2_list: x.sds_section2_list.map((x) => ({
      id: x.id,
      sds_id: x.sds_id,
      sds_product_id: x.sds_product_id,
      classification_system_id: x.classification_system_id,
      material_name: x.material_name,
      item_id: x.item_id,
      category_id: x.category_id,
      deleted_at: x.deleted_at,
      sds_section2_hazard_list: x.sds_section2_hazard_list.map((y) => ({
        id: y.id,
        sds_id: y.sds_id,
        sds_section2_id: y.sds_section2_id,
        hazard_id: y.hazard_id,
        deleted_at: y.deleted_at,
      })),
    })),
    sds_section2_label: {
      id: x.sds_section2_label.id,
      sds_id: x.sds_section2_label.sds_id,
      sds_product_id: x.sds_section2_label.sds_product_id,
      bomb: x.sds_section2_label.bomb,
      fire: x.sds_section2_label.fire,
      fire_circle: x.sds_section2_label.fire_circle,
      gas: x.sds_section2_label.gas,
      corrosion: x.sds_section2_label.corrosion,
      toxic: x.sds_section2_label.toxic,
      exclamation: x.sds_section2_label.exclamation,
      health: x.sds_section2_label.health,
      environment: x.sds_section2_label.environment,
      signal_words: x.sds_section2_label.signal_words,
      signal_word_id: x.sds_section2_label.signal_word_id,
      deleted_at: x.sds_section2_label.deleted_at,
    },
    sds_section3: convertFrontendToApiRequest(x.sds_section3),
    sds_section8_acgih_list: x.sds_section8_acgih_list,
    sds_section8_jsoh_list: x.sds_section8_jsoh_list,
    sds_section8_dnel_dmel_list: x.sds_section8_dnel_dmel_list,
    sds_section8_exposure_control_list: x.sds_section8_exposure_control_list,
    sds_section9_list: x.sds_section9_list.map((s9) => ({
      id: s9.id,
      sds_id: x.sds_id,
      sds_product_id: s9.sds_product_id,
      condition: s9.condition,
      physical_condition: s9.physical_condition,
      form: s9.form,
      deleted_at: s9.deleted_at,
    })),
    sds_section9_flash_point_list: x.sds_section9_flash_point_list.map((f) => ({
      id: f.id,
      sds_id: f.sds_id,
      sds_product_id: f.sds_product_id,
      flash_point_min: f.flash_point_min,
      flash_point_max: f.flash_point_max,
      flash_point_inequality_sign: f.flash_point_inequality_sign,
      unit_id: f.unit_id,
      measurement_method: f.measurement_method,
      created_at: f.created_at,
      updated_at: f.updated_at,
      deleted_at: f.deleted_at,
    })),
    sds_section9_boiling_point_list: x.sds_section9_boiling_point_list.map(
      (f) => ({
        id: f.id,
        sds_id: f.sds_id,
        sds_product_id: f.sds_product_id,
        boiling_point_min: f.boiling_point_min,
        boiling_point_max: f.boiling_point_max,
        boiling_point_inequality_sign: f.boiling_point_inequality_sign,
        unit_id: f.unit_id,
        measurement_method: f.measurement_method,
        created_at: f.created_at,
        updated_at: f.updated_at,
        deleted_at: f.deleted_at,
      })
    ),
    sds_section15_list: x.sds_section15_list.map((section15) => ({
      id: section15.id,
      sds_id: section15.sds_id,
      sds_product_id: section15.sds_product_id,
      law_id: section15.law_id,
      regulation1_id: section15.regulation1_id,
      regulation2_id: section15.regulation2_id,
      regulation3_id: section15.regulation3_id,
      regulation4_id: section15.regulation4_id,
      regulation5_id: section15.regulation5_id,
      applicable_id: section15.applicable_id,
      text: section15.text,
      deleted_at: section15.deleted_at,
      material_list: section15.material_list.map((material) => ({
        id: material.id,
        sds_id: material.sds_id,
        sds_section15_id: material.sds_section15_id,
        material_name: material.material_name,
        deleted_at: material.deleted_at,
        cas_list: material.cas_list.map((cas) => ({
          id: cas.id,
          sds_id: cas.sds_id,
          sds_section15_material_id: cas.sds_section15_material_id,
          cas_number: cas.cas_number,
          deleted_at: cas.deleted_at,
        })),
      })),
    })),
  })),
});
